import React from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import {
    Typography, TextField, ToggleButton, ToggleButtonGroup,
    Chip, Dialog, DialogContent, DialogActions, Button, Divider, CircularProgress,
    Select, MenuItem, FormControl, InputLabel, useMediaQuery, useTheme
} from "@mui/material";
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import { PuffLoader } from "react-spinners";
import { css } from "@emotion/react";
import Colors from "../../components/ui/Colors";
import {
    getProductBatchFiltered, getProductBatch,
    getAllWarehouseProducts, getBatchIndividuals
} from "../../utilityFunctions/WarehouseUtil";

const cssLoader = css`
  display: block;
  margin-left: auto;
  margin-right: auto;
`;

const dateOptions = { day: "numeric", month: "numeric", year: "numeric" };
const dateTimeOptions = { day: "numeric", month: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" };

const PAGE_SIZE = 50;
const EXPIRY_WARNING_DAYS = 30;

let batchCodeFilter = '';
let productFilter = '';
let inWarehouseFilter = null;
let batchIdFilter = null;
let dateFromFilter = null;
let dateToFilter = null;
let currentPageNum = 0;

function expiryColor(expirationDate) {
    if (!expirationDate) return "text.primary";
    const diffDays = Math.ceil((new Date(expirationDate) - new Date()) / (1000 * 60 * 60 * 24));
    if (diffDays < 0) return "error.main";
    if (diffDays <= EXPIRY_WARNING_DAYS) return "warning.main";
    return "text.primary";
}

function fmt(date) {
    if (!date) return "—";
    return new Intl.DateTimeFormat('es-CO', dateOptions).format(new Date(date));
}

function fmtDT(date) {
    if (!date) return "—";
    return new Intl.DateTimeFormat('es-CO', dateTimeOptions).format(new Date(date));
}

function numFmt(n) {
    return new Intl.NumberFormat('es-CO').format(n || 0);
}

export default function BatchesList(props) {
    const { loginUser } = props;

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [loading, setLoading] = React.useState(false);
    const [filterLoading, setFilterLoading] = React.useState(false);
    const [hasNextPage, setHasNextPage] = React.useState(false);
    const [currentPage, setCurrentPage] = React.useState(0);

    const [batchCodeInput, setBatchCodeInput] = React.useState('');
    const [productInput, setProductInput] = React.useState('all');
    const [warehouseToggle, setWarehouseToggle] = React.useState('all');
    const [productsList, setProductsList] = React.useState([]);
    const [batchIdInput, setBatchIdInput] = React.useState('');
    const [dateFromInput, setDateFromInput] = React.useState('');
    const [dateToInput, setDateToInput] = React.useState('');

    const [renderedList, setRenderedList] = React.useState(null);

    const [detailOpen, setDetailOpen] = React.useState(false);
    const [detailLoading, setDetailLoading] = React.useState(false);
    const [detailBatch, setDetailBatch] = React.useState(null);
    const [detailIndividuals, setDetailIndividuals] = React.useState([]);

    const debounceRef = React.useRef(null);

    React.useEffect(() => {
        batchCodeFilter = '';
        productFilter = '';
        inWarehouseFilter = null;
        batchIdFilter = null;
        dateFromFilter = null;
        dateToFilter = null;
        currentPageNum = 0;
        fetchBatches(0, false);
        getAllWarehouseProducts(loginUser.jwtToken).then((products) => {
            if (products) {
                const sorted = [...products].sort((a, b) =>
                    (a.description || '').localeCompare(b.description || '')
                );
                setProductsList(sorted);
            }
        });
    }, []);

    const fetchBatches = async (page, silent) => {
        if (silent) setFilterLoading(true);
        else setLoading(true);

        const data = await getProductBatchFiltered({
            page,
            batchCode: batchCodeFilter || null,
            productDescription: productFilter || null,
            inWarehouse: inWarehouseFilter,
            batchId: batchIdFilter || null,
            dateFrom: dateFromFilter || null,
            dateTo: dateToFilter || null,
        }, loginUser.jwtToken);

        if (silent) setFilterLoading(false);
        else setLoading(false);

        if (!data) return;

        currentPageNum = page;
        setCurrentPage(page);
        setHasNextPage(data.length >= PAGE_SIZE);
        renderList(data);
    };

    const renderList = (data) => {
        if (!data || data.length === 0) {
            setRenderedList(
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 4, textAlign: "center" }}>
                    No se encontraron estibas
                </Typography>
            );
            return;
        }
        setRenderedList(data.map((batch) => (
            <BatchRow key={batch.id} batch={batch} onClick={() => openDetail(batch)} />
        )));
    };

    // Separate component so isMobile is accessed at render time
    const BatchRow = ({ batch, onClick }) => {
        const expColor = expiryColor(batch.expirationDate);
        const clientName = batch.deliveryOrder?.clientName;
        const carPlate = batch.deliveryOrder?.deliveryOrderTransport?.carPlate;

        const statusChip = (
            <Chip
                label={batch.inWarehouse ? "En bodega" : "Fuera"}
                size="small"
                sx={{
                    backgroundColor: batch.inWarehouse ? "#e8f5e9" : "#f5f5f5",
                    color: batch.inWarehouse ? "success.dark" : "text.secondary",
                    fontSize: "0.65rem",
                    fontWeight: "bold",
                }}
            />
        );

        if (isMobile) {
            return (
                <ListItem disablePadding divider>
                    <ListItemButton onClick={onClick} sx={{ px: 1.5, py: 1 }}>
                        <Box sx={{ width: "100%" }}>
                            {/* Row 1: product + status chip */}
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: "bold", flex: 1, mr: 1 }}>
                                    {batch.warehouseProduct?.description || "—"}
                                </Typography>
                                {statusChip}
                            </Box>
                            {/* Row 2: lote + cantidad */}
                            <Box sx={{ display: "flex", gap: 2 }}>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    Lote: <strong>{batch.batchCode || "—"}</strong>
                                </Typography>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    Cant: <strong>{numFmt(batch.productAmount)}</strong>
                                </Typography>
                            </Box>
                            {/* Row 3: vencimiento + cliente */}
                            <Box sx={{ display: "flex", gap: 2, mt: 0.3 }}>
                                <Typography variant="caption" sx={{ color: expColor, fontWeight: expColor !== "text.primary" ? "bold" : "normal" }}>
                                    Vence: {fmt(batch.expirationDate)}
                                </Typography>
                                {clientName && (
                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                        {clientName}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </ListItemButton>
                </ListItem>
            );
        }

        // Desktop row
        return (
            <ListItem disablePadding divider>
                <ListItemButton onClick={onClick} sx={{ px: 1, py: 0.75 }}>
                    <Grid container spacing={1} alignItems="center">
                        <Grid item xs={1}>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>{batch.id}</Typography>
                        </Grid>
                        <Grid item xs={1}>
                            <Typography variant="body2">{batch.batchCode || "—"}</Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography variant="body2">{batch.warehouseProduct?.description || "—"}</Typography>
                        </Grid>
                        <Grid item xs={1} sx={{ textAlign: "center" }}>
                            <Typography variant="body2" sx={{ color: expColor, fontWeight: expColor !== "text.primary" ? "bold" : "normal" }}>
                                {fmt(batch.expirationDate)}
                            </Typography>
                        </Grid>
                        <Grid item xs={1} sx={{ textAlign: "center" }}>
                            <Typography variant="body2">{fmt(batch.date)}</Typography>
                        </Grid>
                        <Grid item xs={1} sx={{ textAlign: "center" }}>
                            <Typography variant="body2">{numFmt(batch.productAmount)}</Typography>
                        </Grid>
                        <Grid item xs={1} sx={{ display: "flex", justifyContent: "center" }}>
                            {statusChip}
                        </Grid>
                        <Grid item xs={2}>
                            <Typography variant="body2" sx={{ color: clientName ? "text.primary" : "text.secondary" }}>
                                {clientName || "—"}
                            </Typography>
                        </Grid>
                        <Grid item xs={1} sx={{ textAlign: "center" }}>
                            <Typography variant="body2" sx={{ color: carPlate ? "text.primary" : "text.secondary" }}>
                                {carPlate || "—"}
                            </Typography>
                        </Grid>
                    </Grid>
                </ListItemButton>
            </ListItem>
        );
    };

    // Filters

    const handleBatchCodeChange = (e) => {
        const val = e.target.value;
        setBatchCodeInput(val);
        batchCodeFilter = val;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchBatches(0, true), 600);
    };

    const handleProductChange = (e) => {
        const val = e.target.value;
        setProductInput(val);
        productFilter = val === 'all' ? '' : val;
        fetchBatches(0, true);
    };

    const handleWarehouseToggle = (e, val) => {
        if (val === null) return;
        setWarehouseToggle(val);
        inWarehouseFilter = val === 'all' ? null : val === 'in';
        fetchBatches(0, true);
    };

    const handleBatchIdChange = (e) => {
        const val = e.target.value.trim();
        setBatchIdInput(val);
        batchIdFilter = val ? parseInt(val) : null;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchBatches(0, true), 600);
    };

    const handleDateFromChange = (e) => {
        setDateFromInput(e.target.value);
        dateFromFilter = e.target.value || null;
        fetchBatches(0, true);
    };

    const handleDateToChange = (e) => {
        setDateToInput(e.target.value);
        dateToFilter = e.target.value || null;
        fetchBatches(0, true);
    };

    const handleClear = () => {
        setBatchCodeInput('');
        setProductInput('all');
        setWarehouseToggle('all');
        setBatchIdInput('');
        setDateFromInput('');
        setDateToInput('');
        batchCodeFilter = '';
        productFilter = '';
        inWarehouseFilter = null;
        batchIdFilter = null;
        dateFromFilter = null;
        dateToFilter = null;
        fetchBatches(0, false);
    };

    const hasActiveFilter = batchCodeInput || productInput !== 'all' || warehouseToggle !== 'all' || batchIdInput || dateFromInput || dateToInput;

    const handlePrev = () => fetchBatches(currentPageNum - 1, false);
    const handleNext = () => fetchBatches(currentPageNum + 1, false);

    // Detail dialog

    const openDetail = async (batchSummary) => {
        setDetailOpen(true);
        setDetailBatch(null);
        setDetailIndividuals([]);
        setDetailLoading(true);
        const [full, individuals] = await Promise.all([
            getProductBatch({ id: batchSummary.id, username: loginUser.accountId }, loginUser.jwtToken),
            getBatchIndividuals(batchSummary.id, loginUser.jwtToken),
        ]);
        setDetailLoading(false);
        setDetailBatch(full || batchSummary);
        setDetailIndividuals(individuals || []);
    };

    const closeDetail = () => {
        setDetailOpen(false);
        setDetailBatch(null);
        setDetailIndividuals([]);
    };

    const CheckpointRow = ({ label, timestamp, username }) => (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1 }}>
            <Box sx={{
                width: 10, height: 10, borderRadius: "50%", mt: "5px", flexShrink: 0,
                backgroundColor: timestamp ? Colors.primary : "#ccc",
            }} />
            <Box>
                <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>{label}</Typography>
                <Typography variant="body2" sx={{ fontWeight: timestamp ? "bold" : "normal", color: timestamp ? "text.primary" : "text.secondary" }}>
                    {timestamp ? fmtDT(timestamp) : "Pendiente"}
                </Typography>
                {username && timestamp && (
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>{username}</Typography>
                )}
            </Box>
        </Box>
    );

    return (
        <Box sx={{ display: "flex", justifyContent: "center", width: "100%", height: "100%" }}>
            <Box sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "start",
                width: { xs: "100%", sm: "95%" },
                height: "100%",
                overflowY: "scroll",
                px: { xs: 1, sm: 0 },
            }}>
                {loading ? <PuffLoader size={100} color={Colors.secondary} css={cssLoader} /> :
                    <Grid container sx={{ mt: 2 }}>

                        {/* Title */}
                        <Grid item xs={12} sx={{ textAlign: "center", mb: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: "bold", color: Colors.primary }}>
                                Registro de Estibas
                            </Typography>
                        </Grid>

                        {/* Filter bar */}
                        <Grid item xs={12}>
                            <Box sx={{
                                display: "flex",
                                gap: { xs: 1.5, sm: 2 },
                                flexWrap: "wrap",
                                alignItems: "flex-end",
                                px: 1,
                                pb: 1,
                                borderBottom: "1px solid #e0e0e0",
                                mb: 1,
                            }}>
                                <TextField
                                    variant="standard"
                                    label="Lote"
                                    value={batchCodeInput}
                                    onChange={handleBatchCodeChange}
                                    sx={{ width: { xs: "100%", sm: 130 } }}
                                    InputProps={{ endAdornment: filterLoading ? <CircularProgress size={14} /> : null }}
                                />
                                <TextField
                                    variant="standard"
                                    label="ID Estiba"
                                    value={batchIdInput}
                                    onChange={handleBatchIdChange}
                                    sx={{ width: { xs: "100%", sm: 90 } }}
                                    inputProps={{ inputMode: "numeric" }}
                                />
                                <TextField
                                    variant="standard"
                                    label="Producción desde"
                                    type="date"
                                    value={dateFromInput}
                                    onChange={handleDateFromChange}
                                    sx={{ width: { xs: "100%", sm: 150 } }}
                                    InputLabelProps={{ shrink: true }}
                                />
                                <TextField
                                    variant="standard"
                                    label="Producción hasta"
                                    type="date"
                                    value={dateToInput}
                                    onChange={handleDateToChange}
                                    sx={{ width: { xs: "100%", sm: 150 } }}
                                    InputLabelProps={{ shrink: true }}
                                />
                                <FormControl variant="standard" sx={{ width: { xs: "100%", sm: 200 } }}>
                                    <InputLabel>Producto</InputLabel>
                                    <Select value={productInput} onChange={handleProductChange}>
                                        <MenuItem value="all">Todos</MenuItem>
                                        {productsList.map((p) => (
                                            <MenuItem key={p.id || p.itemNumber} value={p.description}>
                                                {p.description}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Box>
                                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>Ubicación</Typography>
                                    <ToggleButtonGroup
                                        value={warehouseToggle}
                                        exclusive
                                        onChange={handleWarehouseToggle}
                                        size="small"
                                    >
                                        <ToggleButton value="all" sx={{ textTransform: "none", fontSize: "0.75rem" }}>Todas</ToggleButton>
                                        <ToggleButton value="in" sx={{ textTransform: "none", fontSize: "0.75rem" }}>En bodega</ToggleButton>
                                        <ToggleButton value="out" sx={{ textTransform: "none", fontSize: "0.75rem" }}>Fuera</ToggleButton>
                                    </ToggleButtonGroup>
                                </Box>
                                {hasActiveFilter && (
                                    <Button
                                        onClick={handleClear}
                                        size="small"
                                        variant="outlined"
                                        sx={{ textTransform: "none", borderColor: Colors.primary, color: Colors.primary, alignSelf: "flex-end" }}
                                    >
                                        Limpiar
                                    </Button>
                                )}
                                {/* Expiry legend — hide on very small screens */}
                                <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 2, alignSelf: "flex-end", ml: "auto" }}>
                                    <Typography variant="caption" sx={{ color: "warning.main", fontWeight: "bold" }}>● Vence &lt;30 días</Typography>
                                    <Typography variant="caption" sx={{ color: "error.main", fontWeight: "bold" }}>● Vencida</Typography>
                                </Box>
                            </Box>
                        </Grid>

                        {/* Column headers — desktop only */}
                        {!isMobile && (
                            <Grid item xs={12}>
                                <Box sx={{ px: 1, py: 0.5, backgroundColor: "#f0f0f0", borderRadius: 1, mb: 0.5 }}>
                                    <Grid container spacing={1}>
                                        <Grid item xs={1}><Typography variant="caption" sx={{ fontWeight: "bold", color: "text.secondary" }}>ID</Typography></Grid>
                                        <Grid item xs={1}><Typography variant="caption" sx={{ fontWeight: "bold", color: "text.secondary" }}>Lote</Typography></Grid>
                                        <Grid item xs={3}><Typography variant="caption" sx={{ fontWeight: "bold", color: "text.secondary" }}>Producto</Typography></Grid>
                                        <Grid item xs={1} sx={{ textAlign: "center" }}><Typography variant="caption" sx={{ fontWeight: "bold", color: "text.secondary" }}>Vencimiento</Typography></Grid>
                                        <Grid item xs={1} sx={{ textAlign: "center" }}><Typography variant="caption" sx={{ fontWeight: "bold", color: "text.secondary" }}>Producción</Typography></Grid>
                                        <Grid item xs={1} sx={{ textAlign: "center" }}><Typography variant="caption" sx={{ fontWeight: "bold", color: "text.secondary" }}>Cantidad</Typography></Grid>
                                        <Grid item xs={1} sx={{ textAlign: "center" }}><Typography variant="caption" sx={{ fontWeight: "bold", color: "text.secondary" }}>Estado</Typography></Grid>
                                        <Grid item xs={2}><Typography variant="caption" sx={{ fontWeight: "bold", color: "text.secondary" }}>Cliente</Typography></Grid>
                                        <Grid item xs={1} sx={{ textAlign: "center" }}><Typography variant="caption" sx={{ fontWeight: "bold", color: "text.secondary" }}>Placa</Typography></Grid>
                                    </Grid>
                                </Box>
                            </Grid>
                        )}

                        {/* List */}
                        <Grid item xs={12}>
                            <List disablePadding sx={{ width: "100%" }}>
                                {renderedList}
                            </List>
                        </Grid>

                        {/* Pagination */}
                        <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2, my: 3 }}>
                            <Button
                                onClick={handlePrev}
                                variant="outlined"
                                disabled={currentPageNum === 0}
                                sx={{ textTransform: "none", borderColor: Colors.primary, color: Colors.primary }}
                            >
                                Anterior
                            </Button>
                            <Typography variant="body2">Pág. {currentPageNum + 1}</Typography>
                            <Button
                                onClick={handleNext}
                                variant="outlined"
                                disabled={!hasNextPage}
                                sx={{ textTransform: "none", borderColor: Colors.primary, color: Colors.primary }}
                            >
                                Siguiente
                            </Button>
                        </Grid>
                    </Grid>
                }
            </Box>

            {/* Detail dialog */}
            <Dialog open={detailOpen} onClose={closeDetail} fullWidth maxWidth="sm"
                sx={{ "& .MuiDialog-paper": { borderRadius: 2, mx: { xs: 1, sm: 3 } } }}
            >
                <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
                    {detailLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                            <PuffLoader size={60} color={Colors.secondary} css={cssLoader} />
                        </Box>
                    ) : detailBatch ? (
                        <Grid container spacing={1}>

                            {/* Header */}
                            <Grid item xs={12} sx={{ mb: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: "bold", color: Colors.primary }}>
                                    {detailBatch.warehouseProduct?.description || "Estiba"}
                                </Typography>
                                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                                    {`ID ${detailBatch.id}`}
                                    {detailBatch.batchCode ? ` · Lote ${detailBatch.batchCode}` : ""}
                                </Typography>
                            </Grid>

                            {/* Basic info */}
                            <Grid item xs={12} sx={{ mb: 0.5 }}>
                                <Divider><Typography variant="caption" sx={{ color: "text.secondary" }}>Información</Typography></Divider>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>Cantidad actual</Typography>
                                <Typography variant="body2" sx={{ fontWeight: "bold" }}>{numFmt(detailBatch.productAmount)} uds</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>Cantidad inicial</Typography>
                                <Typography variant="body2">{numFmt(detailBatch.initialProductAmount)} uds</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>Fecha producción</Typography>
                                <Typography variant="body2">{fmt(detailBatch.date)}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>Fecha vencimiento</Typography>
                                <Typography variant="body2" sx={{ color: expiryColor(detailBatch.expirationDate), fontWeight: "bold" }}>
                                    {fmt(detailBatch.expirationDate)}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>Ubicación</Typography>
                                <Box sx={{ mt: 0.3 }}>
                                    <Chip
                                        label={detailBatch.inWarehouse ? "En bodega" : "Fuera"}
                                        size="small"
                                        sx={{
                                            backgroundColor: detailBatch.inWarehouse ? "#e8f5e9" : "#f5f5f5",
                                            color: detailBatch.inWarehouse ? "success.dark" : "text.secondary",
                                            fontWeight: "bold",
                                        }}
                                    />
                                </Box>
                            </Grid>
                            {detailBatch.position && (
                                <Grid item xs={6}>
                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>Posición</Typography>
                                    <Typography variant="body2">
                                        {detailBatch.position}{detailBatch.positionLevel ? ` · Niv. ${detailBatch.positionLevel}` : ""}
                                    </Typography>
                                </Grid>
                            )}

                            {/* Checkpoints: only Entrada/Salida bodega */}
                            <Grid item xs={12} sx={{ mt: 1, mb: 0.5 }}>
                                <Divider><Typography variant="caption" sx={{ color: "text.secondary" }}>Bodega</Typography></Divider>
                            </Grid>
                            <Grid item xs={12}>
                                <CheckpointRow
                                    label="Entrada bodega"
                                    timestamp={detailBatch.supervisorWarehouseCheck}
                                    username={detailBatch.supervisorWarehouseCheckUsername}
                                />
                                <CheckpointRow
                                    label="Salida bodega"
                                    timestamp={detailBatch.supervisorDeliveryCheck}
                                    username={detailBatch.supervisorDeliveryCheckUsername}
                                />
                            </Grid>

                            {/* Delivery order (estiba-level) */}
                            {detailBatch.deliveryOrder && (
                                <>
                                    <Grid item xs={12} sx={{ mt: 1, mb: 0.5 }}>
                                        <Divider><Typography variant="caption" sx={{ color: "text.secondary" }}>Orden de despacho</Typography></Divider>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" sx={{ color: "text.secondary" }}>Cliente</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>{detailBatch.deliveryOrder.clientName || "—"}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" sx={{ color: "text.secondary" }}>Código</Typography>
                                        <Typography variant="body2">{detailBatch.deliveryOrder.orderCode || "—"}</Typography>
                                    </Grid>
                                    {detailBatch.deliveryOrder.deliveryOrderTransport && (
                                        <>
                                            <Grid item xs={6}>
                                                <Typography variant="caption" sx={{ color: "text.secondary" }}>Placa</Typography>
                                                <Typography variant="body2">{detailBatch.deliveryOrder.deliveryOrderTransport.carPlate || "—"}</Typography>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Typography variant="caption" sx={{ color: "text.secondary" }}>Fecha transporte</Typography>
                                                <Typography variant="body2">{fmt(detailBatch.deliveryOrder.deliveryOrderTransport.date)}</Typography>
                                            </Grid>
                                        </>
                                    )}
                                </>
                            )}

                            {/* Individuals */}
                            {detailIndividuals.length > 0 && (
                                <>
                                    <Grid item xs={12} sx={{ mt: 1, mb: 0.5 }}>
                                        <Divider>
                                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                Individuales ({detailIndividuals.length})
                                            </Typography>
                                        </Divider>
                                    </Grid>
                                    {detailIndividuals.map((ind, i) => (
                                        <Grid item xs={12} key={ind.id || i}>
                                            <Box sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                                px: 1.5,
                                                py: 0.75,
                                                backgroundColor: "#f5f5f5",
                                                borderRadius: 1,
                                                mb: 0.5,
                                            }}>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                                        {ind.clientName || "—"}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                        {ind.carPlate ? `Placa ${ind.carPlate}` : "Sin placa"}
                                                        {ind.transportId ? ` · OT ${ind.transportId}` : ""}
                                                        {` · ${fmt(ind.individualsDate)}`}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2" sx={{ fontWeight: "bold", whiteSpace: "nowrap" }}>
                                                    {numFmt(ind.productAmount)} uds
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </>
                            )}

                            {/* Picking orders */}
                            {detailBatch.pickingOrders && detailBatch.pickingOrders.length > 0 && (
                                <>
                                    <Grid item xs={12} sx={{ mt: 1, mb: 0.5 }}>
                                        <Divider>
                                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                Picking ({detailBatch.pickingOrders.length})
                                            </Typography>
                                        </Divider>
                                    </Grid>
                                    {detailBatch.pickingOrders.map((po, i) => (
                                        <Grid item xs={12} key={po.id || i}>
                                            <Box sx={{ display: "flex", gap: 2, px: 1.5, py: 0.75, backgroundColor: "#fafafa", borderRadius: 1, mb: 0.5 }}>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>{fmt(po.createdAt)}</Typography>
                                                    <Typography variant="body2">{po.concept || "—"}</Typography>
                                                </Box>
                                                <Typography variant="body2" sx={{ fontWeight: "bold" }}>{numFmt(po.amount)} uds</Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </>
                            )}

                            {/* Defects */}
                            {detailBatch.defects && detailBatch.defects.length > 0 && (
                                <>
                                    <Grid item xs={12} sx={{ mt: 1, mb: 0.5 }}>
                                        <Divider>
                                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                Averías ({detailBatch.defects.length})
                                            </Typography>
                                        </Divider>
                                    </Grid>
                                    {detailBatch.defects.map((d, i) => (
                                        <Grid item xs={12} key={d.id || i}>
                                            <Box sx={{ display: "flex", gap: 2, px: 1.5, py: 0.75, backgroundColor: "#fff8f8", borderRadius: 1, mb: 0.5 }}>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography variant="caption" sx={{ color: "text.secondary" }}>{fmt(d.timestamp)}</Typography>
                                                    {d.description && <Typography variant="body2">{d.description}</Typography>}
                                                </Box>
                                                <Typography variant="body2" sx={{ fontWeight: "bold", color: "error.main" }}>{numFmt(d.amount)} uds</Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </>
                            )}

                        </Grid>
                    ) : (
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>No se pudo cargar la información</Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={closeDetail} variant="outlined"
                        sx={{ textTransform: "none", borderColor: Colors.primary, color: Colors.primary }}
                    >
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
